// frontend/src/pages/Roadmap.tsx
import React, { useEffect, useState } from 'react';
import {
  Button, Card, Col, Form, Input, Modal, Row, Select, Space,
  Tag, Typography, Popconfirm, Spin, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  RoadmapProject, RoadmapItem, ItemCreate,
  getProjects, getItems, createProject, createItem, updateItem, deleteItem,
} from '../api/roadmap';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PRIORITY_CONFIG = {
  high:   { label: 'Высокий',  color: 'red'    },
  medium: { label: 'Средний',  color: 'orange' },
  low:    { label: 'Низкий',   color: 'green'  },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  plan:        { label: 'Запланировано', color: 'default' },
  in_progress: { label: 'В работе',      color: 'processing' },
  progress:    { label: 'В работе',      color: 'processing' },
  done:        { label: 'Готово',         color: 'success' },
};

const CATEGORY_OPTIONS = [
  { value: 'frontend', label: 'Frontend' },
  { value: 'backend',  label: 'Backend'  },
  { value: 'infra',    label: 'Инфра'    },
  { value: 'other',    label: 'Другое'   },
];

const PRIORITY_ORDER: RoadmapItem['priority'][] = ['high', 'medium', 'low'];

const STATUS_FILTER_OPTIONS = [
  { value: 'all',      label: 'Все статусы'   },
  { value: 'plan',     label: 'Запланировано' },
  { value: 'progress', label: 'В работе'      },
  { value: 'done',     label: 'Готово'        },
];

const Roadmap: React.FC = () => {
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(
    () => localStorage.getItem('roadmap_status_filter') || 'plan'
  );

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    localStorage.setItem('roadmap_status_filter', value);
  };

  // Item modal
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [itemForm] = Form.useForm();

  // Project modal
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectForm] = Form.useForm();

  useEffect(() => {
    getProjects().then(data => {
      setProjects(data);
      if (data.length > 0) setSelectedProjectId(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedProjectId == null) return;
    setLoading(true);
    getItems(selectedProjectId)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [selectedProjectId]);

  const openCreateItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({ priority: 'medium', status: 'plan' });
    setItemModalOpen(true);
  };

  const openEditItem = (item: RoadmapItem) => {
    setEditingItem(item);
    itemForm.setFieldsValue(item);
    setItemModalOpen(true);
  };

  const handleItemSubmit = async (values: ItemCreate) => {
    try {
      if (editingItem) {
        const updated = await updateItem(editingItem.id, values);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        message.success('Задача обновлена');
      } else {
        if (!selectedProjectId) return;
        const created = await createItem(selectedProjectId, values);
        setItems(prev => [...prev, created]);
        message.success('Задача добавлена');
      }
      setItemModalOpen(false);
    } catch {
      message.error('Ошибка при сохранении');
    }
  };

  const handleDeleteItem = async (id: number) => {
    await deleteItem(id);
    setItems(prev => prev.filter(i => i.id !== id));
    message.success('Задача удалена');
  };

  const handleCreateProject = async (values: { name: string; description?: string }) => {
    const project = await createProject(values);
    setProjects(prev => [...prev, project]);
    setSelectedProjectId(project.id);
    setProjectModalOpen(false);
    projectForm.resetFields();
  };

  const filteredItems = statusFilter === 'all'
    ? items
    : items.filter(i => i.status === statusFilter);

  const itemsByPriority = PRIORITY_ORDER.reduce((acc, priority) => {
    acc[priority] = filteredItems.filter(i => i.priority === priority);
    return acc;
  }, {} as Record<string, RoadmapItem[]>);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space>
          <Title level={4} style={{ margin: 0 }}>Roadmap</Title>
          <Select
            style={{ minWidth: 200 }}
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            placeholder="Выберите проект"
          />
          <Button size="small" onClick={() => setProjectModalOpen(true)}>
            + Проект
          </Button>
          <Select
            style={{ minWidth: 160 }}
            value={statusFilter}
            onChange={handleStatusFilter}
            options={STATUS_FILTER_OPTIONS}
          />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateItem}
          disabled={!selectedProjectId}>
          Добавить задачу
        </Button>
      </Space>

      {/* Board */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        PRIORITY_ORDER.map(priority => {
          const config = PRIORITY_CONFIG[priority];
          const group = itemsByPriority[priority] || [];
          if (group.length === 0) return null;
          return (
            <div key={priority} style={{ marginBottom: 24 }}>
              <Space style={{ marginBottom: 8 }}>
                <Tag color={config.color} style={{ fontSize: 14, padding: '2px 10px' }}>
                  {config.label}
                </Tag>
                <Text type="secondary">{group.length} задач</Text>
              </Space>
              <Row gutter={[12, 12]}>
                  {group.map(item => (
                    <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        size="small"
                        title={<Text strong style={{ fontSize: 13 }}>{item.title}</Text>}
                        extra={
                          <Space size={4}>
                            <Button size="small" type="text" icon={<EditOutlined />}
                              onClick={() => openEditItem(item)} />
                            <Popconfirm title="Удалить задачу?" onConfirm={() => handleDeleteItem(item.id)}
                              okText="Да" cancelText="Нет">
                              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        }
                        style={{ height: '100%' }}
                      >
                        {item.description && (
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                            {item.description}
                          </Text>
                        )}
                        <Space size={4} wrap>
                          <Tag color={(STATUS_CONFIG[item.status] || { color: 'default' }).color}>
                            {(STATUS_CONFIG[item.status] || { label: item.status }).label}
                          </Tag>
                          {item.category && <Tag>{item.category}</Tag>}
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
            </div>
          );
        })
      )}

      {/* Item Modal */}
      <Modal
        title={editingItem ? 'Редактировать задачу' : 'Новая задача'}
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        onOk={() => itemForm.submit()}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={itemForm} layout="vertical" onFinish={handleItemSubmit}>
          <Form.Item name="title" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="priority" label="Приоритет" rules={[{ required: true }]}>
                <Select options={Object.entries(PRIORITY_CONFIG).map(([v, c]) => ({ value: v, label: c.label }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Статус" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'plan', label: 'Запланировано' },
                  { value: 'in_progress', label: 'В работе' },
                  { value: 'done', label: 'Готово' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="category" label="Категория">
            <Select allowClear options={CATEGORY_OPTIONS} placeholder="Не указана" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Project Modal */}
      <Modal
        title="Новый проект"
        open={projectModalOpen}
        onCancel={() => setProjectModalOpen(false)}
        onOk={() => projectForm.submit()}
        okText="Создать"
        cancelText="Отмена"
      >
        <Form form={projectForm} layout="vertical" onFinish={handleCreateProject}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Roadmap;
